import BaseDocument from './BaseDocument';

const CHAT_MESSAGE_REQUEST_STATUS = {
  CREATED: 'CREATED',
  COMPLETED: 'COMPLETED',
  FAILED: 'FAILED',
  PENDING: 'PENDING',
};

/**
 * ChatMessageRequest class.
 * Each message scheduled in a chat channel is an instance of this class.
 * @class ChatMessageRequest
 * @extends BaseDocument
 */
class ChatMessageRequest extends BaseDocument {
  /**
   * Get the sender's id.
   * @return {string}
   */
  get userId() {
    return this.data.userId;
  }

  /**
   * Get the id of the channel this message is scheduled to be sent on.
   * @return {string}
   */
  get channelId() {
    return this.data.channelId;
  }

  /**
   * Get the id of the person that actually requested the message schedule.
   * An AC will schedule messages on behalf of a coach, so the from property will be the coach's id,
   * and this one will hold the AC's id.
   * If the coach or the user schedules the message,
   * this will be the same as the <from> property.
   * @return {string}
   */
  get initiatedBy() {
    return this.data.initiatedBy;
  }

  /**
   * Get the text content of the scheduled message.
   * @return {string}
   */
  get text() {
    return this.data.text;
  }

  /**
   * Get the attachments of the scheduled message.
   * @return {Array}
   */
  get attachments() {
    return this.data.attachments || [];
  }

  /**
   * Get the datetime the message was scheduled to be sent on.
   * We should use UTC time for all dates, so that we can sort them correctly.
   * @return {Date}
   */
  get sendAt() {
    return this.data.sendAt?.toDate();
  }

  /**
   * Get the status of the request. Could be "CREATED", "COMPLETED" or "FAILED".
   * @return {string}
   */
  get status() {
    return this.data.status;
  }

  /**
   * Get the time the message schedule request was created.
   * We should use UTC time for all dates, so that we can sort them correctly.
   * @return {Date}
   */
  get createdAt() {
    return this.data.createdAt?.toDate();
  }

  /**
   * Get the coaching activity id related to the scheduled message.
   * @return {Array}
   */
  get activityId() {
    return this.data.activityId;
  }

  // Methods

  /**
   * Edit the message schedule request.
   * @param {string} text New text to be assigned to the message.
   * @param {Array} attachments New attachments to be sent with the message.
   * @param {Date} sendAt New datetime to schedule the message to be sent on.
   * @param {string} status New status of the request.
   * @returns {Promise<void>}
   */
  async updateRequest(text, attachments, sendAt, status, shouldForceSend) {
    await this.updateFields({
      text,
      attachments,
      sendAt,
      ...(status && { status }),
      ...(shouldForceSend && { shouldForceSend }),
    });
  }

  /**
   * Delete the message schedule request.
   * @returns {Promise<void>}
   */
  async deleteRequest() {
    await this.ref.delete();
  }
}

export default ChatMessageRequest;
export {
  CHAT_MESSAGE_REQUEST_STATUS,
};
